import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Pagination, Table } from '@mantine/core';
import dayjs from 'dayjs';
import CustomerInvoiceListItem from './CustomerInvoiceListItem';
import AppFlexbox from '../../common/AppFlexbox';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import { formatUtcDate } from '../../../helpers/format';

const MAX_PAGE_LENGTH = 10;

const HEADER_STYLE = {
  padding: '8px 12px',
  backgroundColor: 'rgba(56, 56, 56, 0.1)'
};

const AMOUNT_HEADER_STYLE = {
  padding: '8px 12px',
  backgroundColor: 'rgba(56, 56, 56, 0.1)',
  textAlign: 'end'
};

const CustomerInvoiceList = ({ invoices = [], isLoading, onViewPayments }) => {
  const [pageIndex, setPageIndex] = useState(1);
  const sortedInvoices = useMemo(
    () =>
      [...invoices].sort(
        (a, b) =>
          dayjs(formatUtcDate(b.createdAt)).valueOf() -
          dayjs(formatUtcDate(a.createdAt)).valueOf()
      ),
    [invoices]
  );
  const totalPages = Math.ceil(sortedInvoices.length / MAX_PAGE_LENGTH);
  const currentPage = Math.min(pageIndex, Math.max(totalPages, 1));

  return (
    <AppStack style={{ gap: 10 }}>
      <AppStack style={{ overflowX: 'auto', gap: 0 }}>
        <Table
          highlightOnHover
          style={{ minWidth: 640 }}
          withRowBorders
          withTableBorder
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={HEADER_STYLE}>
                <AppText style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                  Invoice
                </AppText>
              </Table.Th>
              <Table.Th style={HEADER_STYLE}>
                <AppText style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                  Issued
                </AppText>
              </Table.Th>
              <Table.Th style={AMOUNT_HEADER_STYLE}>
                <AppText style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                  Amount
                </AppText>
              </Table.Th>
              <Table.Th style={HEADER_STYLE}>
                <AppText style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                  Status
                </AppText>
              </Table.Th>
              <Table.Th style={AMOUNT_HEADER_STYLE} />
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {isLoading ? (
              <>
                <CustomerInvoiceListItem />
                <CustomerInvoiceListItem />
              </>
            ) : sortedInvoices.length === 0 ? (
              <Table.Tr>
                <Table.Td
                  colSpan={5}
                  style={{ padding: 40, textAlign: 'center' }}
                >
                  <AppText style={{ fontSize: 14 }} weight={500}>
                    - You have no invoices -
                  </AppText>
                </Table.Td>
              </Table.Tr>
            ) : (
              sortedInvoices
                .slice(
                  (currentPage - 1) * MAX_PAGE_LENGTH,
                  currentPage * MAX_PAGE_LENGTH
                )
                .map((i) => (
                  <CustomerInvoiceListItem
                    key={i.pkRegFormSubmissionInvoice}
                    invoice={i}
                    onViewPayments={onViewPayments}
                  />
                ))
            )}
          </Table.Tbody>
        </Table>
      </AppStack>

      {!isLoading && totalPages > 1 && (
        <AppFlexbox style={{ justifyContent: 'center' }}>
          <Pagination
            color="blue"
            onChange={setPageIndex}
            total={totalPages}
            value={currentPage}
          />
        </AppFlexbox>
      )}
    </AppStack>
  );
};

CustomerInvoiceList.propTypes = {
  invoices: PropTypes.array,
  isLoading: PropTypes.bool,
  onViewPayments: PropTypes.func
};

export default CustomerInvoiceList;
